import { useQuery } from '@tanstack/react-query'
import { z } from 'zod'

import { httpService } from '@/api/v1/http.service'
import type { PaginationParams } from '@/types/params'
import type { DeepCamelCase } from '@/types/utils'
import { formatApiDate } from '@/utils/format-date'
import { requiredString } from '@/utils/zod'

import type { CurriculumAim } from '../constants/curriculum-aim'

type APIResponse = z.infer<typeof responseSchema>

const responseSchema = z.array(
  z.object({
    id: requiredString(),
    name: requiredString(),
    can_be_deleted: z.boolean()
  })
)

const parseResponse = (results: APIResponse): DeepCamelCase<APIResponse> =>
  results.map(item => ({
    id: item.id,
    name: item.name,
    canBeDeleted: item.can_be_deleted
  }))

type Filters = {
  curriculum?: string[]
  educationLevel?: string[]
  aim?: CurriculumAim[]
  schoolYearsFrom?: string
  schoolYearsTo?: string
}

type Params = Filters & PaginationParams

export const useCurriculumIds = (params: Params = {}) =>
  useQuery({
    queryKey: ['panelCurriculumListIds', params],
    staleTime: 60 * 1000,
    queryFn: async () => {
      const response = await httpService.panel.panelCurriculumsList({
        page: 1,
        page_size: 'max',
        id: params.curriculum,
        education_level_id: params.educationLevel,
        aim: params.aim,
        created_after: params.schoolYearsFrom
          ? formatApiDate(new Date(params.schoolYearsFrom))
          : undefined,
        created_before: params.schoolYearsTo
          ? formatApiDate(new Date(params.schoolYearsTo))
          : undefined,
        fetchKeys: {
          id: true,
          name: true,
          can_be_deleted: true
        }
      })

      return parseResponse(responseSchema.parse(response.results))
    }
  })
